'use client'

import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../Icon/Icon'
import importIcon from "../../assets/images/cloud-arrow-down.svg"
import MeetingsAPI from '../../api/MeetingsAPI'
import { ButtonV2 } from '../ButtonV2/ButtonV2'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../Dialog/Dialog'
import googleCalendarInstructions from '../../assets/images/export-google-calendar.gif';
import { CheckIcon, Loader2 } from 'lucide-react'
import { motion } from 'framer-motion'

export default function ImportCalendarButton() {
  const { t } = useTranslation()
  const [importSuccess, setImportSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    setIsLoading(true)
    try {
      const response = await MeetingsAPI.import(file)
      const result = await response.data
      console.log('Import successful:', result)
      setImportSuccess(true)
      setTimeout(() => {
        setIsOpen(false)
        setImportSuccess(false)
      }, 10000)
    } catch (error) {
      console.error('Import failed:', error)
      setImportSuccess(false)
    } finally {
      setIsLoading(false)
    }
    
    event.target.value = ''
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <ButtonV2 variant="outline" className="bg-white h-auto">
            <Icon url={importIcon} />
            <span className="ml-2">{t("import.calendar.modal.title")}</span>
          </ButtonV2>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[700px] w-11/12">
          <DialogHeader>
            <DialogTitle>{importSuccess ? t("import.calendar.success.title") : t("import.calendar.modal.title")}</DialogTitle>
            <DialogDescription>
              {importSuccess ? `${t("import.calendar.success.description")}.` : `${t("import.calendar.modal.description")}.` }
            </DialogDescription>
          </DialogHeader>
          {importSuccess ? (
            <div className="flex flex-col items-center space-y-4 py-4">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5 }}
                className="rounded-full bg-green-500 p-2"
              >
                <CheckIcon className="w-8 h-8 text-white" />
              </motion.div>
              <p className="text-lg font-semibold text-green-600">{t("import.calendar.success.message")}</p>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row gap-6 py-4">
            <div className="flex-1 flex justify-center items-start">
              <img
                src={googleCalendarInstructions}
                alt="How to export Google Calendar"
                className="rounded-md max-w-full h-auto"
              />
            </div>
            <div className="flex-1 space-y-4">
                <ol className="list-decimal list-inside space-y-2">
                    <li>{t("import.calendar.modal.steps.1")}</li>
                    <li>{t("import.calendar.modal.steps.2")}</li>
                    <li>{t("import.calendar.modal.steps.3")}</li>
                    <li>{t("import.calendar.modal.steps.4")}</li>
                    <li>{t("import.calendar.modal.steps.5")}</li>
                </ol>
                <p className="text-sm text-muted-foreground">
                {t("import.calendar.modal.steps.note")}
                </p>
                <ButtonV2 onClick={handleImportClick} disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("import.calendar.modal.button.loading")}
                    </>
                  ) : (
                    t("import.calendar.modal.button")
                  )}
                </ButtonV2>
            </div>
          </div>) }
        </DialogContent>
      </Dialog>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".ics"
        className="hidden"
      />
    </>
  )
}